<script setup lang="ts">
import { ref } from 'vue';
import { getWorkspaceVisibilityType } from '../../../../types/workspace_visibility';
import SideMenuItem from '@/dashboard/vue/components/layout/SideMenu/SideMenuItem.vue';
import Button from '@/dashboard/vue/components/common/Button.vue';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import LockIcon from '@/dashboard/vue/components/icons/LockIcon.vue';
import TeamIcon from '@/dashboard/vue/components/icons/TeamIcon.vue';
import CirclePlus from '@/dashboard/vue/components/icons/CirclePlusIcon.vue';
import SideMenuProject from '@/dashboard/vue/components/layout/SideMenu/SideMenuProject.vue';
import { useSideMenuInner } from '@/dashboard/vue/components/layout/SideMenu/useSideMenuInner';

const { workspaces, sharedProjects, currentUser, createWorkspace, createProject, isCanAddProject } = useSideMenuInner();

const isScrolling = ref(false);
let scrollTimeout: number | null | undefined = null;

const handleScroll = () => {
  isScrolling.value = true;
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }
  scrollTimeout = setTimeout(() => {
    isScrolling.value = false;
  }, 800);
};
</script>

<template>
  <div class="fixed bottom-zero left-zero top-zero flex w-[241px] flex-col max-tablet:absolute">
    <nav
      class="side-menu flex flex-col overflow-x-auto overscroll-contain px-s pb-[32px] pt-m laptop:pt-[95px]"
      :class="{ 'is-scrolling': isScrolling }"
      @scroll="handleScroll"
    >
      <div v-for="workspace in workspaces" :key="workspace.id" class="flex flex-col items-start">
        <SideMenuItem
          :title="workspace.name"
          :hover="true"
          :path="`/w/${workspace.short_link}`"
          :class="'mb-xxs w-full'"
          :title-class="'max-w-[130px]'"
          :emoji="workspace.is_owner ? ' 👑' : undefined"
        >
          <template #icon>
            <LockIcon
              v-if="getWorkspaceVisibilityType(workspace) == 'private'"
              :stroke-class="'stroke-black dark:stroke-gray-500'"
              :fill-class="'fill-black dark:fill-gray-500'"
              class="min-w-[16px]"
            />

            <TeamIcon v-else-if="getWorkspaceVisibilityType(workspace) == 'team'" class="min-w-[16px] stroke-black dark:stroke-gray-500" />
          </template>
          <template #right></template>
        </SideMenuItem>

        <div class="flex w-full flex-col">
          <div v-for="project in workspace.projects" :key="project.id" class="flex justify-start">
            <SideMenuProject :project="project" />
          </div>

          <div v-if="isCanAddProject(workspace)" class="hidden justify-start laptop:flex">
            <SideMenuItem
              title="Create New"
              :action="() => createProject(workspace)"
              :hover="true"
              :class="'ml-[12px] w-full cursor-pointer text-gray-500 dark:text-gray-700'"
            >
              <template #icon>
                <CirclePlus class="ml-[-3px] stroke-gray-450 group-hover:stroke-black dark:stroke-gray-700 dark:group-hover:stroke-gray-500" />
              </template>
              <template #right></template>
            </SideMenuItem>
          </div>
        </div>

        <div class="divider-panel my-s h-[1px] w-[calc(100%+16px)]" />
      </div>

      <div v-if="sharedProjects.length > 0" class="flex flex-col items-start">
        <SideMenuItem :title="'Shared directories'" :hover="false" :class="'mb-xxs w-full'" />

        <div class="flex w-full flex-col gap-xxs">
          <div v-for="project in sharedProjects" :key="project.id" class="flex justify-start">
            <SideMenuProject :project="project" />
          </div>
        </div>

        <div class="divider-panel my-s h-[1px] w-[calc(100%+16px)]" />
      </div>

      <div class="flex flex-col items-start">
        <div class="mb-s mt-xs hidden w-full pr-[9px] laptop:block">
          <Button :on-click="createWorkspace" class="button-primary flex h-[44px] w-full !pb-zero">
            <CirclePlus class="ml-[-3px] mr-xs stroke-white" />
            <span class="text-caption-s text-white">New workspace</span>
          </Button>
        </div>

        <div class="flex w-full flex-col gap-xxs">
          <div class="flex justify-start">
            <SideMenuItem :title="'Account'" :hover="true" :path="'/settings'" :class="'w-full'">
              <template #icon>
                <div class="h-s w-s overflow-hidden rounded-[50%]">
                  <img :src="currentUser?.image" alt="" class="object-cover" />
                </div>
              </template>
              <template #right></template>
            </SideMenuItem>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>
